/**
 * Composable for PDF navigation (pages, view modes)
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { PDFViewMode } from '../types'

export interface UsePDFNavigationOptions {
  totalPages: Ref<number>
  initialPage?: number
  initialViewMode?: PDFViewMode
  onPageChange?: (page: number) => void
  onViewModeChange?: (viewMode: PDFViewMode) => void
}

export interface UsePDFNavigationReturn {
  // State
  currentPage: Ref<number>
  viewMode: Ref<PDFViewMode>

  // Computed
  canGoNext: ComputedRef<boolean>
  canGoPrev: ComputedRef<boolean>
  isFirstPage: ComputedRef<boolean>
  isLastPage: ComputedRef<boolean>

  // Methods
  goToPage: (page: number) => void
  nextPage: () => void
  prevPage: () => void
  firstPage: () => void
  lastPage: () => void
  setViewMode: (mode: PDFViewMode) => void
}

/**
 * Composable for managing PDF navigation
 */
export function usePDFNavigation(options: UsePDFNavigationOptions): UsePDFNavigationReturn {
  const {
    totalPages,
    initialPage = 1,
    initialViewMode = 'continuous',
    onPageChange,
    onViewModeChange,
  } = options

  // State
  const currentPage = ref(initialPage)
  const viewMode = ref<PDFViewMode>(initialViewMode)

  // Computed
  const canGoNext = computed(() => currentPage.value < totalPages.value)
  const canGoPrev = computed(() => currentPage.value > 1)
  const isFirstPage = computed(() => currentPage.value === 1)
  const isLastPage = computed(() => currentPage.value === totalPages.value)

  /**
   * Navigates to a specific page
   */
  function goToPage(page: number): void {
    const validPage = Math.max(1, Math.min(page, totalPages.value))
    if (validPage !== currentPage.value) {
      currentPage.value = validPage
      onPageChange?.(validPage)
    }
  }

  /**
   * Goes to the next page
   */
  function nextPage(): void {
    if (canGoNext.value) {
      goToPage(currentPage.value + 1)
    }
  }

  /**
   * Goes to the previous page
   */
  function prevPage(): void {
    if (canGoPrev.value) {
      goToPage(currentPage.value - 1)
    }
  }

  /**
   * Goes to the first page
   */
  function firstPage(): void {
    goToPage(1)
  }

  /**
   * Goes to the last page
   */
  function lastPage(): void {
    goToPage(totalPages.value)
  }

  /**
   * Sets the view mode
   */
  function setViewMode(mode: PDFViewMode): void {
    if (mode !== viewMode.value) {
      viewMode.value = mode
      onViewModeChange?.(mode)
    }
  }

  return {
    currentPage,
    viewMode,
    canGoNext,
    canGoPrev,
    isFirstPage,
    isLastPage,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setViewMode,
  }
}
